import type {
  CustomWorkbookDetail,
  InnerDesignTemplate,
  RecommendationBody,
} from '@mathflat/domain/MakeCustomWorkbook'
import { PARTITION_TYPE_KO, type PartitionType } from '@mathflat/domain/PrintDocument'
import { PROBLEM_LEVEL_KO } from '@mathflat/domain/Problem'
import {
  REPORT_COMPONENT_KO,
  type ReportComparisonGroup,
  type ReportScoringHistory,
} from '@mathflat/domain/Report'
import type { SchoolType } from '@mathflat/domain/SchoolSystem'
import type { WorkbookSecuringType, WorkbookType } from '@mathflat/domain/Workbook'
import { SIGNATURE_TYPE_KO, type SignatureType, type Workbook } from '@mathflat/domain/Workbook'
import type {
  WorksheetFilterType,
  WorksheetMergeProblemSortType,
  WorksheetTag,
} from '@mathflat/domain/Worksheet'

import type { LocalStorageDataT } from '@/constant/localStorageData'
import { ACADEMY_NAME_TYPE } from '@/contentComponents/CoverEditor/hooks/useCoverDataFieldValuesForm'
import { LEARNING_STEP_NAME } from '@/contentComponents/makeLessonMaterial/(filter)/component/MakeLessonMaterialFilterItem'
import type { PaperUploadService } from '@/contentComponents/makeLessonMaterial/(step1)/widget/MakeByPaper/MakeByPaperUpload/service/PaperUpload.service'
import type { OriginalOption } from '@/contentComponents/makeLessonMaterial/component/WorksheetBuildOptionPanel/Option.type'
import { type GroupInfo, Report } from '@/dto'
import type { WorkbookDesignTemplate } from '@/dto/DesignTemplate/WorkbookCoverDesignTemplate'
import type { MakeWorksheetFilterOption, MakeWorksheetPreviewOptionFilter } from '@/dto/Worksheet'
import type { AggregatedSecuringType } from '@/modules/schooTest2/util/securingTypeUtils'
import {
  type ComparisonOptionKey,
  WORKBOOK_TYPES,
  WORKSHEET_TAGS,
} from '@/page/Lesson/Report/_constants/realtimeReportSettings'
import type { ReportModelV2 } from '@/page/Lesson/Report/_model/report.model'
import type { CreateReportFormValuesV2 } from '@/page/Lesson/Report/CreateUpdateReport/(step1)/types'
import type { SchoolTestPdfAccessProps } from '@/page/Lesson-prepare/PageSchoolTest/components/SchoolTestPdfAccess/SchoolTestPdfAccess.type'
import type { ConceptChapterCustomWorkbookContext } from '@/page/MakeCustomWorkbook/context/conceptChapterCustomWorkbook.context'
import type { ConceptStepCustomWorkbookContext } from '@/page/MakeCustomWorkbook/context/conceptStepCustomWorkbook.context'
import type { SchoolTestStepCustomWorkbookContext } from '@/page/MakeCustomWorkbook/context/schoolTestStepCustomWorkbook.context'
import type { WorkbookProblemChapterCustomWorkbookContext } from '@/page/MakeCustomWorkbook/context/workbookProblemChapterCustomWorkbook.context'
import type { WorkbookProblemStepCustomWorkbookContext } from '@/page/MakeCustomWorkbook/context/workbookProblemStepCustomWorkbook.context'
import type WorksheetOptionsService from '@/page/MakeWorksheet/WorksheetOptions/_service/WorksheetOptions.service'
import { RouteName } from '@/router/RouteName'
import { curriculumCoreService } from '@/service/curriculumCore.service'
import type { TextFormValue } from '@/service/designTemplateController.service'
import type { SchoolTestType } from '@/types/learningContent'
import type { ArithmeticLevel, MakeWorksheetBy } from '@/types/makeWorksheet'

import type { EventSchema } from '../schema/event.schema'
import type { EventPropertiesSchema } from '../schema/eventProperties.schema'

// 교재의 분할 값은 서버 계약상 number 라 리터럴 키 맵을 number 로 조회할 수 있게 넓힌다
const PARTITION_TYPE_KO_BY_NUMBER: Readonly<
  Record<number, (typeof PARTITION_TYPE_KO)[PartitionType]>
> = PARTITION_TYPE_KO

const ARITHMETIC_LEVEL_LABEL = {
  ALL: '전체',
  LOW: '하',
  MID_LOW: '중하',
} as const satisfies Record<ArithmeticLevel, EventPropertiesSchema.학습지_연산_최종['난이도']>

type SchoolName = EventPropertiesSchema.학습지_연산_최종['학교급'][number]

const _countWorkbooksByType = (
  selectedWorkbookIds: number[] | null | undefined,
  scoringHistory: ReportScoringHistory | null | undefined,
) => {
  if (!selectedWorkbookIds?.length || !scoringHistory) {
    return {
      customWorkbookCount: 0,
      schoolWorkbookCount: 0,
      customSignatureWorkbookCount: 0,
      academyGroupWorkbookCount: 0,
      publicWorkbookCount: 0,
    }
  }
  const selectedSet = new Set(selectedWorkbookIds)
  return {
    customWorkbookCount: scoringHistory.customWorkbooks.filter((w) =>
      selectedSet.has(w.studentWorkbookId),
    ).length,
    schoolWorkbookCount: scoringHistory.schoolWorkbooks.filter((w) =>
      selectedSet.has(w.studentWorkbookId),
    ).length,
    customSignatureWorkbookCount: scoringHistory.customSignatureWorkbooks.filter((w) =>
      selectedSet.has(w.studentWorkbookId),
    ).length,
    academyGroupWorkbookCount: scoringHistory.academyGroupWorkbooks.filter((w) =>
      selectedSet.has(w.studentWorkbookId),
    ).length,
    publicWorkbookCount: scoringHistory.publicWorkbooks.filter((w) =>
      selectedSet.has(w.studentWorkbookId),
    ).length,
  }
}

export const SCHOOL_TYPE_KOR = {
  HIGH: '고등학교',
  MIDDLE: '중학교',
} as const

// 주로 eventProperty converter 용도로 사용
// 그 외 converter는 schema에 있는게 응집되어있는게 좋을 것 같아서 거기에서 관리
export const trackingConverter = {
  commonFilter: (filter: RecommendationBody): EventPropertiesSchema.학습지교재만들기_공통필터 => {
    return {
      최대문제수: 'maxProblemCount' in filter ? filter.maxProblemCount : null,
      난이도: 'level' in filter ? PROBLEM_LEVEL_KO[filter.level] : null,
      자동채점: filter.autoScorableOnly,
      문제타입:
        'answerTypes' in filter
          ? filter.answerTypes?.length === 4
            ? '전체'
            : filter.answerTypes?.includes('SINGLE_CHOICE')
              ? '객관식'
              : '주관식'
          : null,
      모의고사포함여부:
        'practiceTestOption' in filter
          ? filter.practiceTestOption === 'EXCLUDE'
            ? '모의고사 제외'
            : filter.practiceTestOption === 'INCLUDE'
              ? '모의고사 포함'
              : '모의고사만'
          : null,
      단원구성:
        'groupingChapterType' in filter
          ? filter.groupingChapterType === 'LITTLE_CHAPTER'
            ? '소단원 단위로 구성'
            : '중단원 단위로 구성'
          : null,
      기존출제문제제외: filter.excludePrevious,
      교육과정외문제제외: filter.excludeOutOfCurriculum,
    }
  },
  customWorkbookFilter: (
    filter: CustomWorkbookFilterParams['filter'],
    originPublicWorkbook?: CustomWorkbookFilterParams['originPublicWorkbook'],
  ): EventPropertiesSchema.내교재필터 => {
    return {
      ...trackingConverter.commonFilter(filter),
      내교재타입:
        filter.customWorkbookType === 'CONCEPT_STEP' ||
        filter.customWorkbookType === 'CONCEPT_CHAPTER'
          ? '단원유형별'
          : filter.customWorkbookType === 'WORKBOOK_PROBLEM_STEP' ||
              filter.customWorkbookType === 'WORKBOOK_PROBLEM_CHAPTER'
            ? '시중교재'
            : '학교별기출',
      학습단계추가여부:
        filter.customWorkbookType === 'CONCEPT_STEP' ||
        filter.customWorkbookType === 'WORKBOOK_PROBLEM_STEP',
      학습단계:
        filter.customWorkbookType !== 'CONCEPT_CHAPTER' &&
        filter.customWorkbookType !== 'WORKBOOK_PROBLEM_CHAPTER'
          ? filter.steps.map((step) => LEARNING_STEP_NAME[step])
          : undefined,
      ...(originPublicWorkbook
        ? trackingConverter.getPublicWorkbookSecuringTypeKor(originPublicWorkbook.securingType)
        : {}),
    }
  },
  표지정보: ({
    designTemplate,
    textFormValue,
    academyNameType,
    gradeSemesterSubjectLabel,
  }: {
    designTemplate: WorkbookDesignTemplate
    textFormValue: TextFormValue
    academyNameType: ValueOf<typeof ACADEMY_NAME_TYPE>
    gradeSemesterSubjectLabel: string
  }): EventPropertiesSchema.표지정보 => {
    return {
      표지디자인: designTemplate.type,
      색상: designTemplate.color,
      폰트: textFormValue.fontFamily,
      폰트크기: textFormValue.fontSize,
      '학년/학기/과목': gradeSemesterSubjectLabel,
      '(앞)대제목': designTemplate.coverData.title?.props.defaultValue,
      '(앞)소제목': designTemplate.coverData.subtitle?.props.defaultValue,
      '(앞)교육기관명':
        academyNameType === ACADEMY_NAME_TYPE.IMAGE
          ? 'image'
          : designTemplate.coverData.academyName?.props.defaultValue,
      '(등)대제목': designTemplate.coverData.sideTitle?.props.defaultValue,
      '(뒤)상-텍스트': designTemplate.coverData.backTopText?.props.defaultValue,
      '(뒤)하-텍스트': designTemplate.coverData.backBottomText?.props.defaultValue,
    }
  },
  시그니처교재생성정보: (
    signatureType: SignatureType,
    gradeSemesterSubjectLabel: string,
    title: string,
  ): EventPropertiesSchema.시그니처교재생성정보 => {
    return {
      시그니처교재타입: SIGNATURE_TYPE_KO[signatureType],
      '학년/학기/과목': gradeSemesterSubjectLabel,
      교재명: title,
    }
  },
  innerDesignTemplate: (data: {
    innerDesignTemplateId: InnerDesignTemplate['id']
    partitionType: CustomWorkbookDetail['partitionType']
  }): EventPropertiesSchema.내지정보 => {
    return {
      내지디자인: data.innerDesignTemplateId,
      문제분할선택: PARTITION_TYPE_KO_BY_NUMBER[data.partitionType],
    }
  },
  getSatExamSubMenuKor: (
    path: string,
  ): EventPropertiesSchema.수능경시관_공통['수능경시관_하위메뉴'] => {
    if (path === RouteName.practiceTest) return '수능 · 모의고사'
    if (path === RouteName.practiceTestPair) return '모의고사 쌍둥이'
    if (path === RouteName.satWorksheet) return '수능특강 · 수능완성'
    if (path === RouteName.policeMilitaryPracticeTest) return '경찰대 · 사관학교'
    if (path === RouteName.policeMilitaryPracticeTestPair) return '경찰대 · 사관학교 쌍둥이'
    if (path === RouteName.kmmExamWorksheet) return 'KMM수학경시대회'
    // 발생하지 않는 케이스지만 기본값 반환
    return '수능 · 모의고사'
  },
  getSchoolTestSubMenuKor: (
    path: string,
  ): EventPropertiesSchema.내신대비_공통['내신대비하위메뉴'] => {
    if (path === RouteName.textbookWorksheet) return '내신대비교과서'
    if (path === RouteName.recommend) return '내신대비추천'
    // 발생하지 않는 케이스지만 기본값 반환
    return '내신대비교과서'
  },
  getPublicWorkbookSecuringTypeKor: (
    securingType: WorkbookSecuringType | undefined,
  ): EventPropertiesSchema.시중교재지원분류 => {
    if (securingType === 'PRIMARY') return { 시중교재지원분류: '1군' }
    if (securingType === 'SECONDARY') return { 시중교재지원분류: '2군' }
    // 발생하지 않는 케이스지만 기본값 반환
    return { 시중교재지원분류: '1군' }
  },
  getWorkbookType: (type: Workbook['type']): EventPropertiesSchema.교재_문제오류신고 => {
    if (type === 'PUBLIC') return { 문제신고종류: '시중교재' }
    if (type === 'SCHOOL') return { 문제신고종류: '교과서' }
    if (type === 'CUSTOM') return { 문제신고종류: '내교재' }
    if (type === 'SIGNATURE' || type === 'CUSTOM_SIGNATURE') return { 문제신고종류: '시그니처교재' }
    if (type === 'ACADEMY_GROUP') return { 문제신고종류: '학원그룹교재' }
    return { 문제신고종류: '내교재' }
  },
  getPublicWorkbookType: (type: WorkbookType): EventPropertiesSchema.시중교재종류 => {
    if (type === 'PUBLIC') return { 시중교재종류: '시중교재' }
    if (type === 'SCHOOL') return { 시중교재종류: '교과서' }
    // 발생하지 않는 케이스지만 기본값 반환
    return { 시중교재종류: '시중교재' }
  },
  worksheetFilterOption: (
    filter: MakeWorksheetFilterOption,
  ): Omit<EventPropertiesSchema.학습지교재만들기_공통필터, '단원구성' | '최대문제수'> => {
    return {
      난이도: 'level' in filter ? PROBLEM_LEVEL_KO[filter.level] : null,
      자동채점: filter.onlyAutoScorable,
      문제타입:
        filter.problemFilterType === 'ALL'
          ? '전체'
          : filter.problemFilterType === 'CHOICE'
            ? '객관식'
            : '주관식',
      모의고사포함여부:
        filter.practiceTest === 'EXCLUDE'
          ? '모의고사 제외'
          : filter.practiceTest === 'INCLUDE'
            ? '모의고사 포함'
            : '모의고사만',
      기존출제문제제외: filter.excludePrevious,
      교육과정외문제제외: filter.excludeOOC,
    }
  },
  worksheetTemplate: (
    previewOption: MakeWorksheetPreviewOptionFilter,
  ): EventPropertiesSchema.학습지_템플릿정보 => {
    return {
      템플릿명: previewOption.layoutType,
      템플릿컬러: previewOption.layoutColor,
      문제간격: previewOption.problemPadding,
      분할: previewOption.partitionType,
      QR: previewOption?.qrFlag,
    }
  },
  getWorksheetType: <T extends MakeWorksheetBy>(type: T): EventPropertiesSchema.학습지종류<T> => {
    return (() => {
      switch (type) {
        case 'CONCEPT':
          return { 학습지종류: '단원유형별' }
        case 'PUBLIC_WORKBOOK':
          return { 학습지종류: '시중교재' }
        case 'PRACTICE_TEST':
          return { 학습지종류: '수능모의고사' }
        case 'SIGNATURE_WORKBOOK':
          return { 학습지종류: '시그니처교재' }
        case 'ACADEMY_GROUP_WORKBOOK':
          return { 학습지종류: '학원그룹교재' }
        case 'SCHOOL_TEST':
          return { 학습지종류: '학교별기출' }
        case 'SCHOOL_TEST_PAPER':
        case 'CUSTOM_PAPER':
          return { 학습지종류: '기출업로드' }
        case 'ONE_CLICK':
          return { 학습지종류: '원클릭복습학습지' }
        case 'MERGED_WORKSHEET':
          return { 학습지종류: '학습지병합' }
        case 'MY_DB_ORIGINAL':
          return { 학습지종류: '기출원본' }
        case 'MY_DB_SIMILAR':
          return { 학습지종류: '기출유사' }
        case 'ARITHMETIC':
          return { 학습지종류: '연산' }
        default:
          return { 학습지종류: type }
      }
    })() as EventPropertiesSchema.학습지종류<T>
  },
  교재만들기_단계이동: (
    filter: CustomWorkbookFilterParams['filter'],
    context: CustomWorkbookContext,
  ): EventPropertiesSchema.내교재만들기_단계이동_학교별기출 | EventPropertiesSchema.내교재필터 => {
    switch (context.contextType) {
      case 'SCHOOL_TEST_PROBLEM_STEP':
        return {
          ...trackingConverter.customWorkbookFilter(filter),
          ...trackingConverter.기출지원분류({
            securingType: context.selectedOriginWorkbooksAggregatedSecuringType,
          }),
        }
      case 'WORKBOOK_PROBLEM_CHAPTER':
      case 'WORKBOOK_PROBLEM_STEP':
        return trackingConverter.customWorkbookFilter(filter, context.originPublicWorkbook)
      default:
        return trackingConverter.customWorkbookFilter(filter)
    }
  },
  학습지만들기_학교별기출_단계이동버튼({
    securingType,
  }: {
    securingType: AggregatedSecuringType
  }): EventPropertiesSchema.학습지만들기_학교별기출_단계이동버튼 {
    return {
      ...trackingConverter.getWorksheetType('SCHOOL_TEST'),
      ...trackingConverter.기출지원분류({ securingType }),
    }
  },
  기출업로드: {
    파일첨부: ({ files }: { files: FileList }): EventPropertiesSchema.기출파일첨부 => {
      if (Array.from(files).every((file) => file.type.includes('pdf'))) {
        return { 기출파일첨부: 'pdf' }
      }
      if (Array.from(files).every((file) => file.type.includes('image'))) {
        return { 기출파일첨부: 'image' }
      }
      return { 기출파일첨부: '혼합' }
    },
    시험정보수정: ({
      initialMetadata,
      finalData,
    }: {
      initialMetadata?: PaperUploadService['initialMetadata']
      finalData: {
        schoolType?: SchoolType | null
        schoolName?: string
        trieKey: string
        schoolTestYear?: number | null
        schoolTestType?: SchoolTestType | null
      }
    }): EventPropertiesSchema.기출업로드시험정보수정 => {
      const changedFields: EventPropertiesSchema.기출업로드시험정보수정['기출업로드시험정보수정'] =
        []

      if (!initialMetadata) {
        return { 기출업로드시험정보수정: changedFields }
      }

      // 학교급 비교
      if (initialMetadata.schoolType !== finalData.schoolType) {
        changedFields.push('학교급')
      }

      // 학년/학기 비교 (trieKey를 통해 비교)
      const initialTrieKeyData = initialMetadata.trieKey
        ? curriculumCoreService.getItemByTrieKey(initialMetadata.trieKey)
        : null
      const finalTrieKeyData = curriculumCoreService.getItemByTrieKey(finalData.trieKey)

      if (
        initialTrieKeyData?.gradeSubject !== finalTrieKeyData?.gradeSubject ||
        initialTrieKeyData?.semester !== finalTrieKeyData?.semester
      ) {
        changedFields.push('학년/학기')
      }

      // 과목 비교 (trieKey를 통해 비교)
      if (initialTrieKeyData?.trieKey !== finalTrieKeyData?.trieKey) {
        changedFields.push('과목')
      }

      // 학교명 비교
      if (initialMetadata.schoolName !== finalData.schoolName) {
        changedFields.push('학교명')
      }

      // 출제연도 비교
      if (initialMetadata.schoolTestYear !== finalData.schoolTestYear) {
        changedFields.push('출제연도')
      }

      // 중간/기말 비교
      if (initialMetadata.schoolTestType !== finalData.schoolTestType) {
        changedFields.push('중간/기말')
      }

      return { 기출업로드시험정보수정: changedFields }
    },
    최종버튼: (
      data: Partial<{
        schoolType: SchoolType
        schoolName: string
        trieKey: string
        schoolTestYear: number
        schoolTestType: SchoolTestType
      }>,
    ): EventPropertiesSchema.기출업로드시험정보 => {
      const trieKeyData = curriculumCoreService.getItemByTrieKey(data.trieKey ?? '')

      const schoolTestTypeKor = {
        MIDTERM: '중간',
        FINAL: '기말',
      } as const

      return {
        기출업로드시험정보: {
          학교급: data.schoolType ? SCHOOL_TYPE_KOR[data.schoolType] : undefined,
          '학년/학기': data.trieKey ?? undefined,
          과목: trieKeyData?.gradeSubject ?? undefined,
          학교명: data.schoolName ?? undefined,
          출제연도: data.schoolTestYear ?? undefined,
          '중간/기말': data.schoolTestType ? schoolTestTypeKor[data.schoolTestType] : undefined,
        },
      }
    },
    원본학습지OCR: (ocrMode: OriginalOption['ocrMode']) => {
      if (ocrMode === 'FAST') return '빠름' as const
      return '정밀' as const
    },
    원본학습지이미지: (imageCorrection: OriginalOption['imageCorrection']) => {
      if (imageCorrection === 'ORIGINAL') return '원본' as const
      return '보정' as const
    },
  },
  학습지만들기_3단계_학습지만들기: {
    단원유형별: ({
      options,
      previewOption,
      conceptList,
      curriculum,
      problemCount,
      추천문제종류,
    }: {
      options: WorksheetOptionsService['options']
      previewOption: WorksheetOptionsService['previewOption']
      conceptList: WorksheetOptionsService['conceptList']
      curriculum?: { 단원정보_유형: string[]; '학년/학기': string[] }
      problemCount: WorksheetOptionsService['problemCount']
      추천문제종류: EventPropertiesSchema.추천문제종류['추천문제종류']
    }): EventPropertiesSchema.학습지_단원유형별_최종 => {
      return {
        ...trackingConverter.getWorksheetType('CONCEPT'),
        ...trackingConverter.worksheetFilterOption(options),
        ...trackingConverter.worksheetTemplate(previewOption),
        '학제/학년': previewOption.grade,
        '학년/학기': curriculum?.['학년/학기'] || [],
        단원정보_유형: curriculum?.['단원정보_유형'] || [],
        세부정답률_최소: options.minRate,
        세부정답률_최대: options.maxRate,
        유형별개념:
          options.selectedConceptIdList.length > 0
            ? conceptList?.map((concept) => concept.conceptName)
            : [],
        소단원별개념:
          options.selectedLittleChapterIdList.length > 0
            ? conceptList?.map((concept) => concept.conceptName)
            : [],
        최대문제수: options.problemCount,
        문제수: problemCount,
        추천문제종류,
      }
    },
    연산: ({
      options,
      previewOption,
      conceptList,
      curriculum,
      problemList,
      problemCount,
      추천문제종류,
    }: {
      options: WorksheetOptionsService['options']
      previewOption: WorksheetOptionsService['previewOption']
      conceptList: WorksheetOptionsService['conceptList']
      curriculum?: { 단원정보_유형: string[]; '학년/학기': string[] }
      problemList: WorksheetOptionsService['problemList']
      problemCount: WorksheetOptionsService['problemCount']
      추천문제종류: EventPropertiesSchema.추천문제종류['추천문제종류']
    }): EventPropertiesSchema.학습지_연산_최종 => {
      return {
        ...trackingConverter.학습지만들기_3단계_학습지만들기.단원유형별({
          options,
          previewOption,
          conceptList,
          curriculum,
          problemCount,
          추천문제종류,
        }),
        ...trackingConverter.getWorksheetType('ARITHMETIC'),
        // 공통필터의 난이도는 비연산 level(기본 3='중') 이라 연산에선 거짓값이다. 3택으로 덮는다
        난이도: ARITHMETIC_LEVEL_LABEL[options.arithmeticLevel ?? 'ALL'],
        요청문항수: options.problemCount,
        실제문항수: problemCount,
        문항수분할값: previewOption.partitionType,
        // 고른 범위가 아니라 실제로 담긴 문항 기준. 문항마다 붙어오는 교육과정 정보를 쓴다
        학교급: [
          ...new Set(
            problemList
              .map((problem) => problem._concept?.schoolName)
              .filter((schoolName): schoolName is SchoolName => !!schoolName),
          ),
        ],
        균등배분여부: options.equalityLevel !== null,
        자동채점여부: options.onlyAutoScorable,
      }
    },
    수능모의고사: ({
      options,
      previewOption,
      conceptList,
      problemCount,
    }: {
      options: WorksheetOptionsService['options']
      previewOption: WorksheetOptionsService['previewOption']
      conceptList: WorksheetOptionsService['conceptList']
      problemCount: WorksheetOptionsService['problemCount']
    }): EventPropertiesSchema.학습지_수능모의고사_최종 => {
      return {
        ...trackingConverter.getWorksheetType('PRACTICE_TEST'),
        ...trackingConverter.worksheetFilterOption(options),
        ...trackingConverter.worksheetTemplate(previewOption),
        유형별개념:
          options.selectedConceptIdList.length > 0
            ? conceptList?.map((concept) => concept.conceptName)
            : [],
        소단원별개념:
          options.selectedLittleChapterIdList.length > 0
            ? conceptList?.map((concept) => concept.conceptName)
            : [],
        문제수: problemCount,
      }
    },
    시중교재: ({
      options,
      previewOption,
      conceptList,
      createParams,
      problemCount,
    }: {
      options: WorksheetOptionsService['options']
      previewOption: WorksheetOptionsService['previewOption']
      conceptList: WorksheetOptionsService['conceptList']
      createParams: WorksheetOptionsService['createParams']
      problemCount: WorksheetOptionsService['problemCount']
    }): EventPropertiesSchema.학습지_시중교재_최종 => {
      return {
        ...trackingConverter.getWorksheetType('PUBLIC_WORKBOOK'),
        ...trackingConverter.worksheetFilterOption(options),
        ...trackingConverter.worksheetTemplate(previewOption),
        ...trackingConverter.getPublicWorkbookType(options.originWorkbook!.type),
        '학제/학년': previewOption.grade,
        유형별개념:
          options.selectedConceptIdList.length > 0
            ? conceptList?.map((concept) => concept.conceptName)
            : [],
        소단원별개념:
          options.selectedLittleChapterIdList.length > 0
            ? conceptList?.map((concept) => concept.conceptName)
            : [],
        교재명: createParams.title,
        ...(options.originWorkbook?.type === 'PUBLIC'
          ? trackingConverter.getPublicWorkbookSecuringTypeKor(options.originWorkbook.securingType)
          : {}),
        쌍둥이지원: options.pairX !== null,
        쌍둥이문제수: options.pairX,
        유사문제수: options.similarX,
        문제수: problemCount,
      }
    },
    시그니처교재: ({
      options,
      previewOption,
      conceptList,
      createParams,
      problemCount,
    }: {
      options: WorksheetOptionsService['options']
      previewOption: WorksheetOptionsService['previewOption']
      conceptList: WorksheetOptionsService['conceptList']
      createParams: WorksheetOptionsService['createParams']
      problemCount: WorksheetOptionsService['problemCount']
    }): EventPropertiesSchema.학습지_시그니처교재_최종 => {
      return {
        ...trackingConverter.getWorksheetType('SIGNATURE_WORKBOOK'),
        ...trackingConverter.worksheetFilterOption(options),
        ...trackingConverter.worksheetTemplate(previewOption),
        '학제/학년': previewOption.grade,
        유형별개념:
          options.selectedConceptIdList.length > 0
            ? conceptList?.map((concept) => concept.conceptName)
            : [],
        소단원별개념:
          options.selectedLittleChapterIdList.length > 0
            ? conceptList?.map((concept) => concept.conceptName)
            : [],
        교재명: createParams.title,
        쌍둥이지원: options.pairX !== null,
        쌍둥이문제수: options.pairX,
        유사문제수: options.similarX,
        문제수: problemCount,
      }
    },
    학원그룹교재: ({
      options,
      previewOption,
      conceptList,
      createParams,
      problemCount,
    }: {
      options: WorksheetOptionsService['options']
      previewOption: WorksheetOptionsService['previewOption']
      conceptList: WorksheetOptionsService['conceptList']
      createParams: WorksheetOptionsService['createParams']
      problemCount: WorksheetOptionsService['problemCount']
    }): EventPropertiesSchema.학습지_학원그룹교재_최종 => {
      return {
        ...trackingConverter.getWorksheetType('ACADEMY_GROUP_WORKBOOK'),
        ...trackingConverter.worksheetFilterOption(options),
        ...trackingConverter.worksheetTemplate(previewOption),
        '학제/학년': previewOption.grade,
        유형별개념:
          options.selectedConceptIdList.length > 0
            ? conceptList?.map((concept) => concept.conceptName)
            : [],
        소단원별개념:
          options.selectedLittleChapterIdList.length > 0
            ? conceptList?.map((concept) => concept.conceptName)
            : [],
        교재명: createParams.title,
        쌍둥이지원: options.pairX !== null,
        쌍둥이문제수: options.pairX,
        유사문제수: options.similarX,
        문제수: problemCount,
      }
    },
    학교별기출: ({
      options,
      previewOption,
      schoolTestData,
      problemCount,
      designTemplateName,
    }: {
      options: MakeWorksheetFilterOption
      previewOption: MakeWorksheetPreviewOptionFilter
      schoolTestData?: LocalStorageDataT['학교별기출학습지_학습지만들기']
      problemCount: WorksheetOptionsService['problemCount']
      designTemplateName?: string
    }): EventPropertiesSchema.학습지_학교별기출_최종 => {
      return {
        ...trackingConverter.getWorksheetType('SCHOOL_TEST'),
        ...trackingConverter.worksheetFilterOption(options),
        ...trackingConverter.worksheetTemplate(previewOption),
        Sch_Exam_Region: schoolTestData?.Sch_Exam_Region,
        Sch_Exam_School: schoolTestData?.Sch_Exam_School,
        Sch_Exam_Grade: schoolTestData?.Sch_Exam_Grade,
        Sch_Exam_Year: schoolTestData?.Sch_Exam_Year,
        Sch_Exam_TwinsSimilar: schoolTestData?.Sch_Exam_TwinsSimilar,
        Sch_Exam_Count: schoolTestData?.Sch_Exam_Count,
        Sch_Exam_Level: schoolTestData?.Sch_Exam_Level,
        Sch_Exam_Temple: designTemplateName || '',
        문제수: problemCount,
        ...trackingConverter.기출지원분류({
          securingType: schoolTestData?.Sch_Exam_SecuringType,
        }),
      }
    },
    기출업로드: ({
      paperType,
      options,
      previewOption,
      problemCount,
      saveOption,
      hasSimilar,
      서술형묶음,
    }: {
      paperType: 'SCHOOL_TEST_PAPER' | 'CUSTOM_PAPER'
      options: MakeWorksheetFilterOption
      previewOption: MakeWorksheetPreviewOptionFilter
      problemCount: WorksheetOptionsService['problemCount']
      saveOption: boolean
      hasSimilar: boolean
      서술형묶음: number
    }): EventPropertiesSchema.학습지_기출업로드_최종 => {
      const trieKey = options.trieKey

      const info = curriculumCoreService.getItemByTrieKey(trieKey)
      const _자료명 =
        paperType === 'SCHOOL_TEST_PAPER'
          ? options.title + '_id:' + options.paperId
          : options.paperTitle + '_id:' + options.paperId
      const schoolTestTypeKor = {
        MIDTERM: '중간',
        FINAL: '기말',
      } as const

      return {
        ...trackingConverter.getWorksheetType(paperType),
        ...trackingConverter.worksheetFilterOption(options),
        ...trackingConverter.worksheetTemplate(previewOption),
        원본타입: paperType === 'SCHOOL_TEST_PAPER' ? '기출 시험지' : '원본 학습지',
        자료명: _자료명,
        원본페이지수: options.selectedPageIndexes.length,
        '학년/학기': info?.label || '',
        학교명: paperType === 'SCHOOL_TEST_PAPER' ? options.schoolName : undefined,
        연도: paperType === 'SCHOOL_TEST_PAPER' ? options.schoolTestYear : undefined,
        '중간/기말':
          paperType === 'SCHOOL_TEST_PAPER'
            ? options.schoolTestType === 'MIDTERM'
              ? '중간'
              : '기말'
            : undefined,
        문제수: problemCount,

        나의DB저장: saveOption,
        유사학습지생성여부: hasSimilar,
        자동채점: options.onlyAutoScorable,
        서술형묶음,

        기출업로드시험정보: {
          학교급: options.schoolType ? SCHOOL_TYPE_KOR[options.schoolType] : undefined,
          '학년/학기': options.trieKey ?? undefined,
          과목: info?.gradeSubject ?? undefined,
          학교명: options.schoolName ?? undefined,
          출제연도: options.schoolTestYear ?? undefined,
          '중간/기말': options.schoolTestType
            ? schoolTestTypeKor[options.schoolTestType]
            : undefined,
        },
      }
    },
    병합학습지: ({
      problemSortType,
      problemCount,
      options,
      previewOption,
    }: {
      problemSortType: WorksheetMergeProblemSortType
      problemCount: WorksheetOptionsService['problemCount']
      options: MakeWorksheetFilterOption
      previewOption: MakeWorksheetPreviewOptionFilter
    }): EventPropertiesSchema.학습지_병합학습지_최종 => {
      return {
        ...trackingConverter.worksheetFilterOption(options),
        ...trackingConverter.worksheetTemplate(previewOption),
        학습지종류: trackingConverter.getWorksheetType('MERGED_WORKSHEET').학습지종류,
        문제수: problemCount,
        학습지병합종류: trackingConverter.학습지병합종류({
          problemSortType,
        }).학습지병합종류,
      }
    },
    나의DB: ({
      type,
      options,
      previewOption,
      problemCount,
      saveOption,
      hasSimilar,
      서술형묶음,
    }: {
      type: 'MY_DB_ORIGINAL' | 'MY_DB_SIMILAR'
      options: MakeWorksheetFilterOption
      previewOption: MakeWorksheetPreviewOptionFilter
      problemCount: WorksheetOptionsService['problemCount']
      saveOption: boolean
      hasSimilar: boolean
      서술형묶음?: number
    }): EventPropertiesSchema.학습지_나의DB_최종 => {
      return {
        ...trackingConverter.getWorksheetType(type),
        ...trackingConverter.worksheetFilterOption(options),
        ...trackingConverter.worksheetTemplate(previewOption),
        문제수: problemCount,
        나의DB저장: saveOption,
        유사학습지생성여부: hasSimilar,
        서술형묶음,
      }
    },
  },

  학습지_문제오류신고: ({
    filterType,
    originWorkbook,
  }: {
    filterType: WorksheetFilterType | null
    originWorkbook?: CustomWorkbookDetail['originWorkbook']
  }): EventPropertiesSchema.학습지_문제오류신고 => {
    const 문제신고종류 = (() => {
      if (!filterType) return '테마별학습지'
      if (filterType === 'WEAK_WORKSHEET') {
        return '학습지별오답학습지'
      }
      if (filterType === 'WEAK_CONCEPT') {
        return '단원유형별오답학습지'
      }
      if (filterType === 'WEAK_PERIOD') {
        return '기간별오답학습지'
      }
      if (filterType === 'ONE_CLICK') {
        return '원클릭복습학습지'
      }
      if (filterType === 'PUBLIC_WORKBOOK' && originWorkbook) {
        if (originWorkbook.type === 'SCHOOL' || originWorkbook.type === 'PUBLIC') {
          return trackingConverter.getWorksheetType('PUBLIC_WORKBOOK').학습지종류
        } else if (originWorkbook.type === 'ACADEMY_GROUP') {
          return trackingConverter.getWorksheetType('ACADEMY_GROUP_WORKBOOK').학습지종류
        } else {
          return trackingConverter.getWorksheetType('SIGNATURE_WORKBOOK').학습지종류
        }
      }

      if (filterType === 'DAILY_WORKSHEET') {
        return '오늘의 학습'
      }
      if (filterType === 'DAILY_WRONG_LEARNING') {
        return filterType
      }

      return trackingConverter.getWorksheetType(filterType).학습지종류
    })()

    return {
      문제신고종류: 문제신고종류,
      ...(originWorkbook && originWorkbook.type === 'PUBLIC'
        ? trackingConverter.getPublicWorkbookSecuringTypeKor(originWorkbook.securingType)
        : {}),
    }
  },

  오답학습지생성: ({
    type,
    securingType,
  }: {
    type: Workbook['type'] | 'ALL'
    securingType?: WorkbookSecuringType | null
  }): EventSchema['문제별오답학습지생성'] | EventSchema['페이지별오답학습지생성'] => {
    const 교재구분 = (() => {
      if (type === 'CUSTOM') return '내교재'
      if (type === 'PUBLIC' || type === 'SCHOOL') {
        return '시중교재'
      }
      if (type === 'ACADEMY_GROUP') return '학원그룹교재'
      return '시그니처교재'
    })()
    return {
      교재구분,
      ...(type === 'PUBLIC' || type === 'SCHOOL'
        ? trackingConverter.getPublicWorkbookType(type)
        : {}),
      ...(securingType && type === 'PUBLIC'
        ? trackingConverter.getPublicWorkbookSecuringTypeKor(securingType)
        : {}),
    }
  },

  기출지원분류: ({
    securingType,
  }: {
    securingType: AggregatedSecuringType
  }): EventPropertiesSchema.기출지원분류 => {
    if (securingType === 'PRIMARY') {
      return { 기출지원분류: '1군' }
    }
    if (securingType === 'SECONDARY') return { 기출지원분류: '2군' }
    if (securingType === 'MIXED') return { 기출지원분류: '혼합' }
    return { 기출지원분류: '없음' }
  },

  학습지병합종류: ({
    problemSortType,
  }: {
    problemSortType: WorksheetMergeProblemSortType
  }): EventPropertiesSchema.학습지병합종류 => {
    if (problemSortType === 'BY_WORKSHEET') {
      return { 학습지병합종류: '사용자지정 순' }
    }
    return { 학습지병합종류: '문제유형 순' }
  },

  열람액션구분: ({
    pdfAction,
  }: {
    pdfAction: SchoolTestPdfAccessProps['pdfAction']
  }): EventPropertiesSchema.열람액션구분 => {
    if (pdfAction === 'DOWNLOAD') return { 열람액션구분: '다운로드' }
    if (pdfAction === 'PREVIEW') return { 열람액션구분: '미리보기' }
    if (pdfAction === 'PRINT') return { 열람액션구분: '인쇄하기' }
    return { 열람액션구분: '다운로드' } // 기본값
  },

  연산여부학습지타입: (isArithmetic: boolean): EventPropertiesSchema.연산여부학습지타입 =>
    isArithmetic ? '연산학습지' : '일반학습지',

  /**
   * 내 학습지 목록(AcademyWorksheetResponse)은 isArithmetic 이 없어 tag 로 폴백한다 —
   * 연산 플로우 기본 태그이지만 사용자가 바꿀 수 있어 근사값이다
   */
  출제학습지타입: (
    worksheets: { isArithmetic?: boolean; tag?: WorksheetTag }[],
  ): EventPropertiesSchema.출제학습지타입 => ({
    학습지타입: trackingConverter.연산여부학습지타입(
      worksheets.length > 0 &&
        worksheets.every(({ isArithmetic, tag }) => isArithmetic ?? tag === 'ARITHMETIC'),
    ),
  }),

  유형분석조회: ({
    groupType,
    groupName,
    유형분석조회,
    schoolFilterTrackingInfo,
  }: {
    groupType: 'lesson-class' | 'student' | 'group' | 'grade' | ''
    groupName: string
    유형분석조회: Pick<
      EventPropertiesSchema.유형분석조회,
      | '학년학기'
      | '시그니처교재'
      | '학원그룹교재'
      | '시중교재'
      | '교과서'
      | '기출연도'
      | '시험범위'
      | '추천유형만보기'
      | '연산만보기'
    >
    schoolFilterTrackingInfo: Pick<
      EventPropertiesSchema.유형분석조회,
      '지역' | '시/군/구' | '학교명'
    >
  }): EventPropertiesSchema.유형분석조회 => {
    const trackingGroupName = groupType === 'lesson-class' ? '반 전체 ' : groupName

    const schoolTestTrackingInfo =
      schoolFilterTrackingInfo.학교명.length === 0
        ? {
            ...schoolFilterTrackingInfo,
            시험범위: '' as EventPropertiesSchema.유형분석조회['시험범위'],
            기출연도: '',
          }
        : schoolFilterTrackingInfo

    return {
      '유형분석조회 대상 유형': trackingGroupName,
      ...유형분석조회,
      ...schoolTestTrackingInfo,
    }
  },

  보고서생성대상: (groupInfo: GroupInfo) => {
    if (groupInfo.data.type === 'grade') {
      return groupInfo.data.schoolGrade.koLabel
    }
    if (groupInfo.data.type === 'lesson-class') {
      return '반 전체'
    }
    if (groupInfo.data.type === 'student') {
      return groupInfo.data.name
    }
    return ''
  },

  보고서상세항목: ({
    report,
    scoringHistory,
  }: {
    report: Report
    scoringHistory?: ReportScoringHistory | null
  }): EventPropertiesSchema.보고서상세항목 => {
    const workbookCounts = _countWorkbooksByType(
      report.learningHistory.studentWorkbookIds,
      scoringHistory,
    )

    const WORKSHEET_TAG_LABELS = WORKSHEET_TAGS.map((t) => t.label)

    const _worksheetTags = report.learningHistory.worksheetTagFlags
      ? report.learningHistory.worksheetTagFlags
          .map((t) => WORKSHEET_TAGS.find((item) => item.key === t)?.label ?? '')
          .filter(Boolean)
      : WORKSHEET_TAG_LABELS

    const _studentWorksheetCount = report.learningHistory.studentWorksheetIds?.length

    return {
      개정: report.revision === 'CURRICULUM_15' ? '15개정' : '22개정',
      기간: report.type === 'MONTHLY' ? '월 선택' : '기간 선택',
      학습기록_태그: _studentWorksheetCount ? undefined : _worksheetTags,
      ...(_studentWorksheetCount && {
        학습기록_학습지수: _studentWorksheetCount,
        학습기록_내교재수: workbookCounts.customWorkbookCount,
        학습기록_교과서수: workbookCounts.schoolWorkbookCount,
        학습기록_시그니처교재수: workbookCounts.customSignatureWorkbookCount,
        학습기록_학원그룹교재수: workbookCounts.academyGroupWorkbookCount,
        학습기록_시중교재수: workbookCounts.publicWorkbookCount,
      }),
      프리미엄학생앱기록: report.learningHistory.selfLearningFlag,
      내용구성: report.componentFlags
        .map((value) => Report.OLD_COMPONENT_LIST.find((item) => item.value === value)?.label ?? '')
        .filter(Boolean),
      가산점: `${report.extraScore}%`,
    }
  },

  보고서상세항목V2: ({
    formValues,
    aiCommentContent,
    scoringHistoryCount,
  }: {
    formValues: CreateReportFormValuesV2
    aiCommentContent?: string
    scoringHistoryCount?: {
      selectedCustomWorkbookCount: number
      selectedSchoolWorkbookCount: number
      selectedCustomSignatureWorkbookCount: number
      selectedAcademyGroupWorkbookCount: number
      selectedPublicWorkbookCount: number
    }
  }): EventPropertiesSchema.보고서상세항목 => {
    const componentLabels = formValues.components
      .map((selectedKey) => REPORT_COMPONENT_KO[selectedKey])
      .filter((key): key is ValueOf<typeof REPORT_COMPONENT_KO> => Boolean(key))

    const convertComparisonGroup = (
      value: typeof formValues.comparisonGroup,
    ): EventPropertiesSchema.보고서상세항목['비교군'] => {
      if (value === 'NATIONAL') return '전국'
      if (value === 'LESSON_CLASS') return '반'
      if (value === 'GRADE') return '학년'
      return '미사용'
    }

    const _worksheetTags = formValues.worksheetTags
      ? formValues.worksheetTags
          .map((t) => WORKSHEET_TAGS.find((item) => item.key === t)?.label ?? '')
          .filter(Boolean)
      : WORKSHEET_TAGS.map((t) => t.label)

    const _workbookTypes = formValues.workbookTypes
      ? formValues.workbookTypes
          .map((t) => WORKBOOK_TYPES.find((item) => item.key === t)?.label ?? '')
          .filter(Boolean)
      : WORKBOOK_TYPES.map((t) => t.label)

    const _studentWorksheetCount = formValues.studentWorksheetIds?.length

    return {
      개정: '전체',
      기간: formValues.type === 'MONTHLY' ? '월 선택' : '기간 선택',
      학습기록_태그: _studentWorksheetCount ? undefined : [..._worksheetTags, ..._workbookTypes],
      ...(_studentWorksheetCount && {
        학습기록_학습지수: _studentWorksheetCount,
        학습기록_내교재수: scoringHistoryCount?.selectedCustomWorkbookCount ?? 0,
        학습기록_교과서수: scoringHistoryCount?.selectedSchoolWorkbookCount ?? 0,
        학습기록_시그니처교재수: scoringHistoryCount?.selectedCustomSignatureWorkbookCount ?? 0,
        학습기록_학원그룹교재수: scoringHistoryCount?.selectedAcademyGroupWorkbookCount ?? 0,
        학습기록_시중교재수: scoringHistoryCount?.selectedPublicWorkbookCount ?? 0,
      }),
      프리미엄학생앱기록: formValues.includeSelfLearning,
      내용구성: componentLabels,
      가산점: `${formValues.additionalScore as number}%`,
      선생님의견: formValues.components.includes('COMMENT')
        ? formValues.aiCommentUsed
          ? 'AI 작성'
          : '직접작성'
        : null,
      '선생님의견(작성내용)': aiCommentContent,
      비교군: convertComparisonGroup(formValues.comparisonGroup),
    }
  },

  보고서설정: (report: Report): EventPropertiesSchema.보고서설정 => {
    return {
      제목: report.title,
      선생님코멘트: report.comment,
      학부모앱공개여부: report.opened ? '공개' : '비공개',
    }
  },

  실시간보고서설정: ({
    used,
    worksheetTags,
    workbookTypes,
    includeSelfLearning,
    components,
    comparisonGroup,
  }: {
    used: boolean
    worksheetTags: string[] | null
    workbookTypes: string[] | null
    includeSelfLearning: boolean
    components: string[] | null
    comparisonGroup: ComparisonOptionKey | ReportComparisonGroup | null
  }): EventPropertiesSchema.실시간보고서설정 => {
    const convertComparisonGroup = (
      value: ComparisonOptionKey | ReportComparisonGroup | null,
    ): EventPropertiesSchema.실시간보고서설정['비교군'] => {
      if (value === 'NATIONAL') return '전국'
      if (value === 'LESSON_CLASS') return '반'
      if (value === 'GRADE') return '학년'
      return '미사용'
    }

    const WORKSHEET_TAG_LABELS = WORKSHEET_TAGS.map((t) => t.label)
    const WORKBOOK_TYPE_LABELS = WORKBOOK_TYPES.map((t) => t.label)

    const _worksheetTags = worksheetTags
      ? worksheetTags
          .map((t) => WORKSHEET_TAGS.find((item) => item.key === t)?.label ?? '')
          .filter(Boolean)
      : WORKSHEET_TAG_LABELS
    const _workbookTypes = workbookTypes
      ? workbookTypes
          .map((t) => WORKBOOK_TYPES.find((item) => item.key === t)?.label ?? '')
          .filter(Boolean)
      : WORKBOOK_TYPE_LABELS

    return {
      사용여부: used ? '사용' : '미사용',
      '학습기록(학습지)': _worksheetTags,
      '학습기록(교재)': _workbookTypes,
      프리미엄학생앱기록: includeSelfLearning,
      내용구성: components ?? [],
      비교군: convertComparisonGroup(comparisonGroup),
    } as const
  },

  보고서공유대상_개인_또는_일괄: (
    groupInfo: GroupInfo,
    isPersonal: boolean,
  ): EventPropertiesSchema.보고서공유대상_개인_또는_일괄 => {
    if (isPersonal) {
      return { 보고서공유대상_개인_또는_일괄: '개인공유' }
    }

    return {
      보고서공유대상_개인_또는_일괄:
        groupInfo.data.type === 'student' ? '일괄공유(개인)' : '일괄공유(전체)',
    }
  },

  보고서타입: (report: ReportModelV2): EventPropertiesSchema.보고서타입 => {
    if (report.type === 'REAL_TIME') return { 보고서타입: '실시간 보고서' }
    if (report.type === 'CUSTOM') return { 보고서타입: '신규 보고서' }
    return { 보고서타입: '기존 보고서' }
  },

  원클릭보고서설정: (
    hasRank: boolean,
    rankOptions: ('NATION' | 'ACADEMY' | 'GRADE' | 'LESSONCLASS')[],
  ): EventSchema['원클릭보고서만들기_보고서만들기_Click'] => {
    if (hasRank) {
      return {
        평균등수: '추가',
        추가대상: rankOptions.map((value) => {
          if (value === 'NATION') {
            return '전국'
          }
          if (value === 'ACADEMY') {
            return '학원'
          }
          return '학교/반'
        }),
      }
    }

    return {
      평균등수: '추가안함',
      추가대상: undefined as never,
    }
  },

  worksheetShareSettingStatus: (setting: {
    allWorksheetShareEnabled: boolean
    details: { MY_DB: { enabled: boolean } }
  }): EventPropertiesSchema.학습지공개설정_상태['학습지 설정 상태'] => {
    if (!setting.allWorksheetShareEnabled) return '모두 비공개 (전체 OFF)'
    if (setting.details.MY_DB.enabled) return '모두 공개 (전체 ON + 원본 ON)'
    return '원본만 비공개 (전체 ON + 원본 OFF)'
  },
}

type CustomWorkbookFilterParams = {
  filter: RecommendationBody
  originPublicWorkbook?:
    | WorkbookProblemChapterCustomWorkbookContext['originPublicWorkbook']
    | WorkbookProblemStepCustomWorkbookContext['originPublicWorkbook']
}

type CustomWorkbookContext =
  | ConceptChapterCustomWorkbookContext
  | ConceptStepCustomWorkbookContext
  | WorkbookProblemChapterCustomWorkbookContext
  | WorkbookProblemStepCustomWorkbookContext
  | SchoolTestStepCustomWorkbookContext
